import { LoadingOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Button } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, CloseIcon } from 'components/Icons';
import { MenuButton, useMessage } from 'components/ui';
import { DAMAGE_CONFIRM as DAMAGE_CONFIRM_RULE } from 'utils/access-control/rules';
import { useMutation } from 'utils/apollo';
import { RenderWithAccess } from 'utils/usePermissions';

const SET_DAMAGE_CONFIRMED = gql`
  mutation ConfirmDamage($branchGlobalId: UUID!, $confirm: Boolean!) {
    confirmDamage(branchGlobalId: $branchGlobalId, confirm: $confirm) {
      id
      globalId
      branchGlobalId
      branchSlug
      version
      confirmed
    }
  }
`;

export const ConfirmDamageToggle = ({
  damage: { id, confirmed: isConfirmed = false },
  type,
  addLeftMargin = true,
  className = '',
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [saving, setSaving] = useState(false);

  const [setConfirmDamage] = useMutation(SET_DAMAGE_CONFIRMED, {
    onCompleted({ confirmDamage: { branchSlug, version, confirmed } }) {
      const label = `${branchSlug.toUpperCase()}-${version}`;
      message.success(
        confirmed
          ? t('damage.damageConfirmedSuccess', 'Damage {{label}} confirmed.', { label })
          : t('damage.damageUnconfirmedSuccess', 'Damage {{label}} unconfirmed.', { label })
      );
    },
  });

  const handleSetConfirm = async confirm => {
    setSaving(true);
    await setConfirmDamage({
      variables: {
        branchGlobalId: id,
        confirm,
      },
    });
    setSaving(false);
  };

  function render() {
    if (saving) {
      return type === 'menu' ? (
        <MenuButton icon={<LoadingOutlined />} type="link" disabled>
          {t('components.saving', 'Saving…')}
        </MenuButton>
      ) : (
        <Button disabled>
          {t('components.saving', 'Saving…')} <LoadingOutlined />
        </Button>
      );
    }

    if (type === 'menu') {
      return isConfirmed ? (
        <MenuButton icon={<CloseIcon />} type="link" onClick={() => handleSetConfirm(false)}>
          {t('damage.unconfirmDamage', 'Unconfirm Damage')}
        </MenuButton>
      ) : (
        <MenuButton
          icon={<CheckIcon addLeftMargin={addLeftMargin} className={className} />}
          type="link"
          onClick={() => handleSetConfirm(true)}
        >
          {t('damage.confirmDamage', 'Confirm Damage')}
        </MenuButton>
      );
    }

    return isConfirmed ? (
      <Button onClick={() => handleSetConfirm(false)}>
        <CloseIcon />
        {t('damage.unconfirmDamage', 'Unconfirm Damage')}
      </Button>
    ) : (
      <Button onClick={() => handleSetConfirm(true)} className={className}>
        <CheckIcon addLeftMargin={addLeftMargin} />
        {t('damage.confirmDamage', 'Confirm Damage')}
      </Button>
    );
  }

  // FIXME: Update all instances of this component to remove `<RenderWithAccess .../>` wrapper
  return <RenderWithAccess rules={[DAMAGE_CONFIRM_RULE]}>{render()}</RenderWithAccess>;
};
